import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export interface SummaryItem {
  /** Qué dato es: "Nombre", "Correo". */
  label: string;
  /** El valor capturado. */
  value: ReactNode;
  /**
   * Qué hacer al pulsar "Editar". Si se omite, la fila no ofrece corrección
   * — para datos que a esa altura ya no se pueden cambiar.
   */
  onEdit?: () => void;
}

export interface SummaryListProps {
  items: readonly SummaryItem[];
  editLabel?: string;
  className?: string;
}

/**
 * El repaso de lo capturado antes de confirmar.
 *
 * Es un `<dl>` y no una tabla ni una pila de `<div>`: cada fila es
 * literalmente un término y su definición, y esa semántica es la que hace
 * que un lector de pantalla lea "Correo: anahi@…" en vez de dos textos
 * sueltos que hay que emparejar de memoria.
 *
 * El "Editar" de cada fila no es un adorno: un resumen sin salida obliga a
 * retroceder paso a paso adivinando en cuál estaba el dato equivocado. El
 * botón dice a dónde va en su texto accesible ("Editar correo"), porque
 * cuatro botones llamados "Editar" seguidos no se distinguen al recorrer
 * la pantalla con un lector.
 */
export function SummaryList({
  items,
  editLabel = "Editar",
  className,
}: SummaryListProps) {
  return (
    <dl
      className={cn(
        "grid divide-y divide-ds-border rounded-2xl border border-ds-border bg-white/50",
        className,
      )}
    >
      {items.map((item) => (
        <div
          key={item.label}
          // Una fila por dato, en UNA línea. Apilar etiqueta sobre valor
          // duplicaba el alto del resumen y hacía que la card diera un
          // salto al llegar al último paso.
          className="flex items-baseline gap-3 px-4 py-2"
        >
          <dt className="w-16 shrink-0 text-[11px] font-medium text-ds-ink-3">
            {item.label}
          </dt>
          {/* `min-w-0` + `break-words`: un correo largo se parte en vez de
              desbordar la card por la derecha en móvil. */}
          <dd className="min-w-0 flex-1 break-words text-sm text-ds-ink">
            {item.value}
          </dd>

          {item.onEdit && (
            <button
              type="button"
              onClick={item.onEdit}
              className="shrink-0 rounded-ds-sm text-[11px] font-medium text-ds-violet-pop underline underline-offset-2 transition hover:text-ds-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50"
            >
              {editLabel}
              <span className="sr-only"> {item.label.toLowerCase()}</span>
            </button>
          )}
        </div>
      ))}
    </dl>
  );
}
